import KoobPlaceholder from './KoobPlaceholder';
import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from '../utils/cn';
import KoobId from './KoobId';
import { useInSettingsModal } from './KoobSettingsModal';

export type KoobPageHeaderCrumb = {
  label: React.ReactNode;
  /** No `to`: shown as plain text. */
  to?: string;
};

export type KoobPageHeaderTab = {
  label: React.ReactNode;
  /** Absolute path: the header matches it against the URL itself. */
  to: string;
  /** Shown as a neutral pill after the label (a total). */
  count?: number;
  /** Shown as an orange pill after the label (unread items). */
  badge?: number;
  /** Active only on an exact match, not on its sub-pages. */
  end?: boolean;
  /** Shown greyed out, not a link (a section with nothing in it yet). */
  disabled?: boolean;
  /**
   * Second-level tabs, shown in a row below while this tab is active. The
   * tab is active on any of them; its `to` is usually the first one's.
   */
  tabs?: KoobPageHeaderTab[];
};

type KoobPageHeaderProps = {
  /** `undefined` while loading: a placeholder bar shows, the tab reads "Koob". */
  title?: React.ReactNode;
  /** Browser tab title, when `title` is not plain text. */
  documentTitle?: string;
  subtitle?: React.ReactNode;
  /**
   * The record's id, first on the subtitle line. Every page about one named
   * record passes it; records without a name carry it in the title instead
   * ("Booking 633296"), and reference data (settings) shows none.
   */
  id?: string | number;
  /** The parent pages, not the current one. */
  breadcrumbs?: KoobPageHeaderCrumb[];
  actions?: React.ReactNode;
  tabs?: KoobPageHeaderTab[];
};

/**
 * Sets the browser tab title: "<title> – Koob", or "Koob" while unknown.
 * Pages without a KoobPageHeader (full-screen ones) call it themselves.
 */
export function useDocumentTitle(title?: string) {
  useEffect(() => {
    document.title = title ? `${title} – Koob` : 'Koob';
  }, [title]);
}

function Crumbs({ items }: { items: KoobPageHeaderCrumb[] }) {
  const parent = items[items.length - 1];

  return (
    <nav aria-label="Breadcrumb" className="text-sm text-k-p">
      {/* Phone: the parent page only, as a back link. */}
      {parent.to ? (
        <Link to={parent.to} className="sm:hidden text-k-p hover:text-k-h">
          <i className="fa-regular fa-chevron-left mr-1.5 text-xs" />
          {parent.label}
        </Link>
      ) : null}
      <ol className="hidden sm:flex flex-wrap items-center gap-x-2 m-0 p-0 list-none">
        {items.map((crumb, index) => (
          // No gap on the li: TO's global CSS gives every li a zero-width
          // ::before, which a gap would push 8px off the title's edge.
          <li key={index} className="flex items-center">
            {index > 0 && (
              <i className="fa-regular fa-chevron-right mr-2 text-[10px] text-gray-300" />
            )}
            {crumb.to ? (
              <Link to={crumb.to} className="text-k-p hover:text-k-h">
                {crumb.label}
              </Link>
            ) : (
              <span>{crumb.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Pill({ value, tone }: { value: number; tone: 'count' | 'badge' }) {
  return (
    <span
      className={cn(
        'ml-2 inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums',
        tone === 'badge'
          ? 'bg-orange-500 text-k-h'
          : 'bg-gray-100 text-gray-600',
      )}
    >
      {value > 999 ? '999+' : value}
    </span>
  );
}

const trimSlash = (path: string) => path.replace(/\/$/, '') || '/';

// Matched here rather than with NavLink: DMC's router beta and TO's 6.3 give
// NavLink different className APIs.
function isActive(tab: KoobPageHeaderTab, pathname: string): boolean {
  if (tab.tabs?.length) {
    return tab.tabs.some((child) => isActive(child, pathname));
  }
  const current = trimSlash(pathname);
  const target = trimSlash(tab.to.split('?')[0]);
  return tab.end
    ? current === target
    : current === target || current.startsWith(`${target}/`);
}

function Tab({
  tab,
  active,
  level = 1,
}: {
  tab: KoobPageHeaderTab;
  active: boolean;
  level?: 1 | 2;
}) {
  const content = (
    <>
      {tab.label}
      {tab.count !== undefined && <Pill value={tab.count} tone="count" />}
      {tab.badge ? <Pill value={tab.badge} tone="badge" /> : null}
    </>
  );
  if (tab.disabled) {
    return (
      <span
        aria-disabled="true"
        className={cn(
          'inline-flex cursor-not-allowed items-center whitespace-nowrap text-sm font-medium text-gray-300',
          level === 1
            ? 'border-b-2 border-transparent px-1 py-3'
            : 'px-3 py-1.5',
        )}
      >
        {content}
      </span>
    );
  }
  return (
    <Link
      to={tab.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'inline-flex items-center whitespace-nowrap text-sm font-medium',
        level === 1 && 'border-b-2 px-1 py-3',
        level === 1 &&
          (active
            ? 'border-orange-500 text-orange-600'
            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'),
        level === 2 && 'rounded-md px-3 py-1.5',
        level === 2 &&
          (active
            ? 'bg-gray-100 text-k-h'
            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'),
      )}
    >
      {content}
    </Link>
  );
}

/**
 * The page header of both apps: every page states its title, subtitle,
 * parent pages, actions and tabs. Filters, cards and lists belong in the
 * content, below it.
 */
export default function KoobPageHeader({
  title,
  documentTitle,
  subtitle,
  id,
  breadcrumbs,
  actions,
  tabs,
}: KoobPageHeaderProps) {
  const { pathname } = useLocation();
  // In the settings modal the body scrolls under it: title and tabs stay.
  const sticky = useInSettingsModal();
  const activeTab = tabs?.find((tab) => isActive(tab, pathname));
  const activeSubTab = activeTab?.tabs?.find((tab) => isActive(tab, pathname));
  const shownTab = activeSubTab ?? activeTab;
  const pageTitle =
    documentTitle ?? (typeof title === 'string' ? title : undefined);
  // "Audit log · Asia Voyages – Koob": the active tab first, so browser tabs
  // and history tell a page's tabs apart.
  useDocumentTitle(
    pageTitle && typeof shownTab?.label === 'string'
      ? `${shownTab.label} · ${pageTitle}`
      : pageTitle,
  );

  const hasTabs = Boolean(tabs?.length);
  // A long row of tabs scrolls sideways: bring the current one into view,
  // in each row.
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const reveal = () =>
      headerRef.current?.querySelectorAll('nav').forEach((nav) => {
        const current = nav.querySelector<HTMLElement>('[aria-current="page"]');
        if (!current) return;
        const left = current.offsetLeft - nav.offsetLeft;
        if (left + current.offsetWidth > nav.scrollLeft + nav.clientWidth)
          nav.scrollLeft = left + current.offsetWidth - nav.clientWidth + 24;
        else if (left < nav.scrollLeft) nav.scrollLeft = left - 24;
      });
    reveal();
    // Again once the web font is in: it widens the labels.
    document.fonts?.ready.then(reveal);
  }, [pathname]);

  // Sticky in the settings modal: what sticks below it (a table's toolbar)
  // reads its height from --koob-sticky-top, kept current as it resizes.
  useEffect(() => {
    const header = headerRef.current;
    const scroller = header?.parentElement;
    if (!sticky || !header || !scroller) return undefined;
    const observer = new ResizeObserver(() =>
      scroller.style.setProperty(
        '--koob-sticky-top',
        `${header.offsetHeight}px`,
      ),
    );
    observer.observe(header);
    return () => {
      observer.disconnect();
      scroller.style.removeProperty('--koob-sticky-top');
    };
  }, [sticky]);
  const subTabs = activeTab?.tabs?.length ? activeTab.tabs : undefined;
  // With the title, not before: an id beside the loading bar reads as noise.
  const shownId = title === undefined || title === null ? undefined : id;

  return (
    <header
      ref={headerRef}
      className={cn(
        'bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 pt-5',
        hasTabs ? 'pb-0' : 'pb-5',
        sticky && 'sticky top-0 z-sticky',
      )}
    >
      <div className="flex flex-col gap-1.5">
        {breadcrumbs?.length ? <Crumbs items={breadcrumbs} /> : null}
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="flex min-w-0 flex-col gap-1">
            {title === undefined || title === null ? (
              <KoobPlaceholder className="h-7 w-56 max-w-full rounded" />
            ) : (
              <h1 className="m-0 text-2xl sm:text-[28px] sm:leading-9 font-semibold tracking-[-0.01em] text-k-h break-words">
                {title}
              </h1>
            )}
            {Boolean(subtitle || shownId) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-k-p">
                <KoobId id={shownId} />
                {subtitle}
              </div>
            )}
          </div>
          {Boolean(actions) && (
            <div className="flex flex-wrap items-center gap-3">{actions}</div>
          )}
        </div>
      </div>
      {tabs && hasTabs && (
        <nav
          aria-label="Tabs"
          className="-mb-px mt-3 flex gap-6 overflow-x-auto"
        >
          {tabs.map((tab) => (
            <Tab key={tab.to} tab={tab} active={tab === activeTab} />
          ))}
        </nav>
      )}
      {subTabs && (
        <nav
          aria-label="Section tabs"
          className="-mx-4 sm:-mx-6 lg:-mx-8 flex gap-1 overflow-x-auto border-t border-gray-200 px-4 sm:px-6 lg:px-8 py-2"
        >
          {subTabs.map((tab) => (
            <Tab
              key={tab.to}
              tab={tab}
              active={tab === activeSubTab}
              level={2}
            />
          ))}
        </nav>
      )}
    </header>
  );
}
